/**
 * Phase 50 Plan 05 — Locale-stable day-granular date picker.
 *
 * Three native <select> controls (Day / Month / Year) with English labels
 * hardcoded. Built because <input type="date"> leaks browser-locale month
 * names on Czech machines (Petra UAT 2026-05-27 — Phase 48 rejected the
 * native control for the same reason).
 *
 * Used by Plan 50-09 (Breach Dashboard from/to filters).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

function buildYears(): number[] {
  const now = new Date().getUTCFullYear();
  return Array.from({ length: 12 }, (_, i) => now + 1 - i);
}

function parseValue(value: string): { day: string; month: string; year: string } {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return { day: '', month: '', year: '' };
  return {
    year: m[1] ?? '',
    month: m[2] ?? '',
    day: String(parseInt(m[3] ?? '0', 10)),
  };
}

function buildValue(day: string, month: string, year: string): string {
  if (!day || !month || !year) return '';
  const dd = String(day).padStart(2, '0');
  return `${year}-${month}-${dd}`;
}

export interface DayMonthYearPickerProps {
  /** YYYY-MM-DD or empty string */
  value: string;
  /** Fires with YYYY-MM-DD when all three selects have a value; empty string otherwise. */
  onChange: (next: string) => void;
  /** Accessible label used as the <fieldset> aria-label / legend. */
  label?: string;
  /** Optional id prefix for the three select elements (a11y). */
  idPrefix?: string;
  disabled?: boolean;
}

export function DayMonthYearPicker({
  value,
  onChange,
  label,
  idPrefix = 'dmy',
  disabled = false,
}: DayMonthYearPickerProps) {
  const nowYear = new Date().getUTCFullYear();
  const years = useMemo(buildYears, [nowYear]);

  // Internal state tracks the three parts independently so partial selections
  // (e.g. Day only) can accumulate before the full YYYY-MM-DD is emitted.
  const [parts, setParts] = useState(() => parseValue(value));

  // Sync internal state when the controlled value changes from outside.
  useEffect(() => {
    setParts(parseValue(value));
  }, [value]);

  const handleChange = useCallback(
    (field: 'day' | 'month' | 'year', fieldValue: string) => {
      const next = { ...parts, [field]: fieldValue };
      setParts(next);
      onChange(buildValue(next.day, next.month, next.year));
    },
    [parts, onChange],
  );

  return (
    <fieldset className="inline-flex items-center gap-1" aria-label={label}>
      {label && <legend className="sr-only">{label}</legend>}
      <select
        id={`${idPrefix}-day`}
        value={parts.day}
        disabled={disabled}
        onChange={(e) => handleChange('day', e.target.value)}
        className="border border-gray-300 rounded px-2 py-1 text-sm"
        aria-label="Day"
      >
        <option value="">—</option>
        {Array.from({ length: 31 }, (_, i) => String(i + 1)).map((d) => (
          <option key={d} value={d}>
            {d}
          </option>
        ))}
      </select>
      <select
        id={`${idPrefix}-month`}
        value={parts.month}
        disabled={disabled}
        onChange={(e) => handleChange('month', e.target.value)}
        className="border border-gray-300 rounded px-2 py-1 text-sm"
        aria-label="Month"
      >
        <option value="">—</option>
        {MONTHS.map((name, idx) => {
          const mm = String(idx + 1).padStart(2, '0');
          return (
            <option key={mm} value={mm}>
              {name}
            </option>
          );
        })}
      </select>
      <select
        id={`${idPrefix}-year`}
        value={parts.year}
        disabled={disabled}
        onChange={(e) => handleChange('year', e.target.value)}
        className="border border-gray-300 rounded px-2 py-1 text-sm"
        aria-label="Year"
      >
        <option value="">—</option>
        {years.map((y) => (
          <option key={y} value={String(y)}>
            {y}
          </option>
        ))}
      </select>
    </fieldset>
  );
}
